import * as React from "react"
import {useMemo, useRef} from "react"
import {useSignal} from "@preact/signals-react";
import MUIDataTable from "mui-datatables";

import {IconSeeker} from "@ui-seeker/core";
import {useTableSeekerColumns, useTableSeekerSearch} from "./@hooks";
import MuiTableTitleSeeker from "./Title";
import MUIDataTableActionsToolbar from "./ActionsToolbar";
import MUIDataTableSearch from "./TableSearch";
import MUIDataTableThemeProvider from "./ThemeProvider";
import AppTableExportMenu from "../../AppTableExportMenu";
import {reportTableChange} from "@analytics/tableEvents";


export default function TableSeeker({ data=[], columns=[], title, subtitle, titleProps={}, renderExcelButton, exportMenuProps={}, ...props }) {
    const { buildColumns } = useTableSeekerColumns(data, columns)
    const { handleSearch } = useTableSeekerSearch()
    const downloadLoading = useSignal(false)
    const isCustomExcel = !!(renderExcelButton || exportMenuProps?.onDownload)
    const rowsPerPage = useSignal(props?.options?.rowsPerPage || 10)
    const tableContainerRef = useRef(null)

    const myColumns = useMemo(() => {
        return buildColumns()
    }, [ JSON.stringify(columns), JSON.stringify(data)])

    return <MUIDataTableThemeProvider theme={props?.theme}>
        <div ref={tableContainerRef}>
            <MUIDataTable
                data={data}
                columns={myColumns}
                {...props}
                title={<MuiTableTitleSeeker title={title} subtitle={subtitle} {...titleProps} />}
                options={{
                elevation: 2,
                selectableRows: 'none',
                customSearch: (searchQuery, currentRow, columns) =>
                    handleSearch(searchQuery, currentRow, columns, props?.options?.caseSensitive ?? false),
                customSearchRender: (searchText, onSearch, onHide, options) => <MUIDataTableSearch
                    searchText={searchText}
                    onSearch={onSearch}
                    onHide={onHide}
                    options={options}
                />,
                ...props?.options,

                // Analytics for the one page still on this component (Monitoring › Results). The
                // events are shared with `AppTableSeeker`, the wrapper the other ~70 tables use, so
                // both emit the same names and props — see `@analytics/tableEvents`.
                onTableChange: (action, tableState) => {
                    reportTableChange(action, tableState, title)
                    if (props?.options?.onTableChange) props.options.onTableChange(action, tableState)
                },

                rowsPerPage: rowsPerPage.value,
                onChangeRowsPerPage: newRowsPerPage => {
                    rowsPerPage.value = newRowsPerPage
                    if (props?.options?.onRowsPerPageChange) {
                        props.options.onChangeRowsPerPage(newRowsPerPage)
                    }
                },

                ...data.length > 10 && {
                    tableBodyHeight: `${props?.options?.tableBodyHeight || 800}px`
                },

                ...props?.options?.customToolbar && {
                    customToolbar: displayData => <MUIDataTableActionsToolbar>
                        {props.options.customToolbar(displayData)}
                    </MUIDataTableActionsToolbar>
                },

                ...props?.options?.customToolbarSelect && {
                    customToolbarSelect: (selectedRows, displayData, setSelectedRows) => {
                        const selectedData = selectedRows.data.map(item => data[item.dataIndex])
                        return props.options.customToolbarSelect(selectedData, setSelectedRows, selectedRows, displayData)
                    }
                },

                ...props?.options?.enhancedSetRowProps && {
                    setRowProps: (row, dataIndex, rowIndex) => {
                        const dataRow = data[dataIndex] || {}
                        return props.options.enhancedSetRowProps(dataRow, dataIndex, rowIndex, row)
                    }
                },

                textLabels: {
                    ...props?.textLabels,
                    toolbar: {
                        downloadCsv: isCustomExcel ? "" : "Download Excel",
                        ...props?.textLabels?.toolbar,
                    }
                },

                ...(isCustomExcel || props?.options?.onDownload ? {
                    download: (!isCustomExcel && downloadLoading.value) ? 'disabled' : true,
                    ...(isCustomExcel
                        ? { onDownload: () => false }
                        : {
                            onDownload: () => {
                                downloadLoading.value = true
                                props.options.onDownload()
                                    .finally(() => downloadLoading.value = false)
                                return false
                            }
                        }
                    )
                } : { download: false })
            }}
            components={{
                ...props?.components,
                icons: {
                    DownloadIcon: renderExcelButton
                        ?? (exportMenuProps?.onDownload
                            ? () => <AppTableExportMenu {...exportMenuProps} />
                            : () => downloadLoading.value
                                ? <IconSeeker icon="spinner" isSpin={true} />
                                : <IconSeeker icon="down-to-line" sx={{ fontSize: "1.25rem"}} {...props?.options?.excelButtonProps} />
                        ),
                    ...props?.components?.icons
                }
            }}
        />
        </div>
    </MUIDataTableThemeProvider>
}
